// components/StickyNote.js - Unified component for both panels
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './StickyNote.css';
import { useWindowOperations } from '../hooks/useWindowOperations';
import TagDropdown from './MainPanel/TagDropdown';
import { getTagTooltip, getDomainTooltip } from './utils/tagTooltips';
import { sendNotesToAI, sendStickySubmenuToAI } from '../api/aiNotesToContent';
import { uploadNoteImage } from '../api/notesApi';
import ConfirmModal from './ConfirmModal';








const MIN_SIZE = { width: 50, height: 50 };

const StickyNote = ({ 
  sticky, 
  updateSticky, 
  removeSticky, 
  maxZIndex, 
  onFocus,
  tagDomains = {},
  onToggleDisplayMode,
  isSelected, // Changed from isPinned
  onSelectChange, // Changed from onPinChange
  isInMainPanel = false,  // Default to false (right panel) for backward compatibility
  registerWithZManager,   // Optional for z-index registration
  notePublicId,
  workspacePublicId,
  ...props
}) => {
  const {
    isExpanded,
    isMinimized,
    startDrag,
    startResize,
    toggleMinimize,
    toggleExpand
  } = useWindowOperations({ sticky, updateSticky, onFocus, isInMainPanel });
  const [localTitle, setLocalTitle] = useState(sticky.title || '');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [customPromptMode, setCustomPromptMode] = useState(false);
  const [customPromptText, setCustomPromptText] = useState("");
  const aiMenuRef = useRef(null);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [pendingAIOutput, setPendingAIOutput] = useState(null);
  const [aiError, setAiError] = useState(null);
   const [textSize, setTextSize] = useState('small'); // 'small', 'medium', 'large'
   const [isEditing, setIsEditing] = useState(false);
   const [perspectiveEnabled, setPerspectiveEnabled] = useState(false); // RIGHT - starts disabled  
   const [imageZoom, setImageZoom] = useState(1); // 1 = 100%, 1.5 = 150%, etc.


   // NEW: Add state for drop menu on sticky
  const [showStickyDropMenu, setShowStickyDropMenu] = useState(false);
  const [stickyDropPosition, setStickyDropPosition] = useState({ x: 0, y: 0 });
  const [droppedTextContent, setDroppedTextContent] = useState('');

  const formatText = (text) => {
  if (!text) return '';
  
  return text
    // Headers
    .replace(/\n/g, '<br/>')                             // line breaks
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')              // ### Header 3
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')               // ## Header 2
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')                // # Header 1
    
    // Text formatting
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')     // **bold**
    .replace(/\*(.*?)\*/g, '<em>$1</em>')                 // *italic*
    .replace(/`(.*?)`/g, '<code>$1</code>')               // `code`
    .replace(/__(.*?)__/g, '<u>$1</u>')                   // __underline__
    .replace(/~~(.*?)~~/g, '<del>$1</del>')               // ~~strikethrough~~
    .replace(/\^\^(.*?)\^\^/g, '<mark>$1</mark>')         // ^^highlight^^
    
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>') // [text](url)
    .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>')     // Auto-link URLs
    
    // Lists (improved)
    .replace(/^- (.+)$/gm, '<li>$1</li>')                 // - bullet items
    .replace(/^\* (.+)$/gm, '<li>$1</li>')                // * bullet items  
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')             // 1. numbered items
    //.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')            // wrap in <ul>
    
    // Checkboxes
    .replace(/^\[ \] (.+)$/gm, '<label><input type="checkbox" disabled> $1</label>') // [ ] unchecked
    .replace(/^\[x\] (.+)$/gm, '<label><input type="checkbox" checked disabled> $1</label>') // [x] checked
    
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>') // ```code blocks```
    
    // Quotes
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')  // > quote
    
    // Horizontal rules
    .replace(/^---$/gm, '<hr>')                           // --- horizontal line
    
    // Line breaks (do this last)
    
};

  const AI_TASKS = {
    summarize: "Summarize the following content.",
    rephrase: "Rephrase the content in clearer language.",
    outline: "Convert this content into a structured outline.",
    custom: "__custom__"
  };

  const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
};


  // Handle pin toggle (prevent sticky auto-minimize)
// Replace handlePinToggle with:
const handleSelectToggle = (e) => {
  e.stopPropagation();
  if (onSelectChange) {
    onSelectChange(sticky.id, !isSelected);
  }
};

const togglePerspective = () => {
  setPerspectiveEnabled(prev => !prev);
};

  // Handle display mode toggle (send to list view)
  const handleDisplayModeToggle = (e) => {
    e.stopPropagation();
    onToggleDisplayMode(sticky.id, 'list');
  };

  // Handle sticky deletion
  const handleDelete = async () => {
      try {
        if (!sticky.id) return;  // ← Check for sticky ID instead
        setIsDeleteModalOpen(false);
        await removeSticky(sticky.id);
      } catch (error) {
        console.error('Error deleting sticky:', error);
      }
    };

  // Add this useEffect to load tag domains




  useEffect(() => {
    if (showAIMenu) {
      const handleClickOutside = (e) => {
        if (aiMenuRef.current && !aiMenuRef.current.contains(e.target)) {
          setShowAIMenu(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAIMenu]);

  useEffect(() => {
  setLocalTitle(sticky.title || '');
}, [sticky.title]);

// Add tag handler functions
const handleDomainChange = useCallback((domain) => {
  if (Object.keys(tagDomains).length === 0 || !tagDomains[domain]) {
   updateSticky(sticky.id, { 
      tag1: domain,
      tag2: '' 
    });
    return;
  }
  
  const availableTags = getTagsForDomain(domain);
  const firstTag = availableTags.length > 0 ? availableTags[0] : '';
  
  updateSticky(sticky.id, { 
    tag1: domain,
    tag2: firstTag
  });
}, [sticky.id, updateSticky,  tagDomains]);

const handleTagChange = useCallback((tag) => {
  updateSticky(sticky.id, { tag2: tag });
}, [sticky.id, updateSticky]);

const getTagsForDomain = useCallback((domain) => {
  return domain ? tagDomains[domain] || [] : [];
}, [tagDomains]);

const getDomainOptions = useMemo(() => {
  return Object.keys(tagDomains);
}, [tagDomains]);




  const handleAISelection = async (task) => {
    if (task === '__custom__') {
      setCustomPromptMode(true); // Show input overlay
      setShowAIMenu(false);
      return;
    }

 
  
    const inputSticky = {
      id: sticky.id,
      type: sticky.type,
      content: sticky.content,
    };
  
    try {
      // Clear any previous errors or outputs
      setAiError(null);
      setPendingAIOutput(null);
      setIsProcessingAI(true); // 🟢 START spinner
      
      const response = await sendStickySubmenuToAI([inputSticky], task);
  
      if (!response) {
        setAiError("AI did not return a result.");
        return;
      }
  
      setPendingAIOutput(response); // Don't commit yet — wait for user decision
    } catch (error) {
      console.error("AI Error:", error);
      console.error("Error response status:", error.response?.status);
      console.error("Error response data:", error.response?.data);
      console.error("Error message:", error.message);
      
      // Generate user-friendly error message based on specific error types
      let errorMessage = "Failed to process content with AI.";
      
    // Check for token-related errors in either the message or response
    if (error.message === 'Insufficient token balance' || 
        (error.response?.data?.detail && error.response.data.detail.includes('out of tokens'))) {
      errorMessage = "⚠️ Out of tokens. Please <a href='https://www.notestruct.com/tokens' target='_blank'>click here</a> to purchase more tokens.";
    } else if (error.message === 'Authentication failed') {
      errorMessage = "⚠️ Authentication error. Please log in again.";
    } else if (error.message === 'AI composition failed') {
      // Check if this might actually be a token error being masked as a general error
      if (error.response?.status === 500) {
        // Server error - may be due to token issues
        console.log("Server error - checking for potential token issues");
        errorMessage = "⚠️ Service error. This may be due to insufficient tokens. <a href='https://www.notestruct.com/tokens' target='_blank'>Purchase tokens here</a>.";
      } else {
        errorMessage = "⚠️ AI service error. Please try again later.";
      }
    } else if (error.message && error.message.includes('rate limit')) {
      errorMessage = "⚠️ Rate limit exceeded. Please wait a moment and try again.";
    } else if (error.response?.status === 402) {
      // Direct check for payment required status code as backup
      errorMessage = "⚠️ Out of tokens. Please <a href='https://www.notestruct.com/tokens' target='_blank'>click here</a> to purchase more tokens.";
    } else if (error.response?.status === 401) {
      // Authentication failed status code
      errorMessage = "⚠️ Authentication error. Please log in again.";
    } else if (error.response?.status === 400) {
      // Bad request (e.g., no stickies provided)
      errorMessage = "⚠️ Invalid request. Please ensure note contains content.";
    } else if (error.response?.status === 429) {
      // Rate limiting error
      errorMessage = "⚠️ Rate limit exceeded. Please wait a moment and try again.";
    } else if (error.response?.status === 500) {
      // Server error (e.g., OpenAI API error)
      errorMessage = "⚠️ Service error. This may be due to insufficient tokens. <a href='https://www.notestruct.com/tokens' target='_blank'>Purchase tokens here</a>.";
    }
          
      setAiError(errorMessage);
    } finally {
      setIsProcessingAI(false);
    }
  };
  
  // NEW: Handle drag over on sticky
  const handleStickyDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent container from handling
    e.dataTransfer.dropEffect = 'copy';
  };

  // NEW: Handle drop on sticky
  const handleStickyDrop = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent container from handling
    
    const droppedText = e.dataTransfer.getData('text/plain') || 
                       e.dataTransfer.getData('text/html') || '';
    
    if (!droppedText.trim()) return;

    // Calculate position relative to sticky
    const stickyRect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - stickyRect.left;
    const relativeY = e.clientY - stickyRect.top;
    
    setStickyDropPosition({ x: relativeX, y: relativeY });
    setDroppedTextContent(droppedText);
    setShowStickyDropMenu(true);
  };

  // NEW: Handle sticky drop menu actions
  const handleStickyDropAction = (action) => {
    setShowStickyDropMenu(false);
    
    switch(action) {
      case 'paste':
        // Append to existing content
        updateSticky(sticky.id, {
          content: sticky.content + '\n\n' + droppedTextContent
        });
        break;
      case 'replace':
        // Replace existing content
        updateSticky(sticky.id, {
          content: droppedTextContent
        });
        break;
      case 'prepend':
        // Add to beginning
        updateSticky(sticky.id, {
          content: droppedTextContent + '\n\n' + sticky.content
        });
        break;
    }
    setDroppedTextContent('');
  };

  // Add click outside handler for sticky drop menu
  useEffect(() => {
    if (showStickyDropMenu) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.sticky-drop-menu')) {
          setShowStickyDropMenu(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showStickyDropMenu]);
  
// Add this function before the return statement
const getTextSizeValue = (size) => {
  switch(size) {
    case 'small': return '18px';
    case 'medium': return '20px';
    case 'large': return '25px';
    default: return '18px';
  }
};

const toggleTextSize = () => {
  const sizes = ['small', 'medium', 'large'];
  const currentIndex = sizes.indexOf(textSize);
  const nextIndex = (currentIndex + 1) % sizes.length;
  setTextSize(sizes[nextIndex]);
};

const getTextSizeIcon = (size) => {
  switch(size) {
    case 'small': return 'A⁻';
    case 'medium': return 'A';
    case 'large': return 'A⁺';
    default: return 'A';
  }
};

// Add this function inside StickyNote component
const handlePaste = async (e) => {
  const items = e.clipboardData?.items;
  if (!items) return;
  
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf('image') !== -1) {
      e.preventDefault();
      e.stopPropagation();
      
      const blob = items[i].getAsFile();
      
      try {
        console.log('🖼️ STEP 1: Detected image paste');
        console.log('🖼️ STEP 2: Uploading to notePublicId:', notePublicId);
        console.log('🖼️ STEP 2: Sticky ID:', sticky.id);
        
        const result = await uploadNoteImage(
            workspacePublicId,
            sticky.id,
            blob
          );
        
        console.log('🖼️ STEP 3: Upload response:', result);
        console.log('🖼️ STEP 4: Image URL:', result.image_url);
        console.log('🖼️ STEP 4: Type:', result.type);
        
        console.log('🖼️ STEP 5: Updating sticky...');
        updateSticky(sticky.id, {
          type: 'image',
          image_url: result.image_url
        });
        
        console.log('🖼️ STEP 6: Sticky updated!');
        console.log('🖼️ Current sticky type:', sticky.type);
        console.log('🖼️ Current sticky image_url:', sticky.image_url);
        
      } catch (error) {
        console.error('❌ Failed to upload image:', error);
        console.error('❌ Error details:', error.response?.data);
        alert('Failed to upload image. Please try again.');
      }
      
      break;
    }
  }
}; 

// copy text
const handleCopyContent = async () => {
  try {
    await navigator.clipboard.writeText(sticky.content || '');
    // Optional: Show brief feedback (you could add a toast notification here)
  } catch (err) {
    console.error('Failed to copy text:', err);
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = sticky.content || '';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
};
// Build expansion prompts using tooltip content
const buildExpansionPrompt = (tagType, userContent) => {
  const tooltip = getTagTooltip(sticky.tag1, tagType);
  
  if (!tooltip) {
    return `Expand this ${tagType} description: ${userContent}`;
  }

  // Extract definition and example from tooltip
  const parts = tooltip.split('\n\nExample: ');
  const definition = parts[0] || `${tagType} content`;
  const example = parts[1] || '';
  
  return `Take this brief ${tagType} note and expand it into a full ${tagType} description.

Definition: ${definition}
${example ? `Example format: "${example}"` : ''}

User's brief note: "${userContent}"

Expand this following the definition and example format above.`;
};

// Get tag-aware menu items
// Replace your existing getTagAwareMenuItems function with this:
const getTagAwareMenuItems = () => {
  const baseItems = [
    { label: '✂️ Summarize', task: AI_TASKS.summarize },
    { label: '🧠 Rephrase', task: AI_TASKS.rephrase },
    { label: '🗂 Turn into outline', task: AI_TASKS.outline },
    { label: '➕ Custom Prompt...', task: AI_TASKS.custom }
  ];

  const expansionItems = [];
  
  // Get available tags for current domain
  const availableTags = getTagsForDomain(sticky.tag1);
  
  // If we have both domain and specific tag, and tooltip exists
  if (sticky.tag1 && sticky.tag2 && availableTags.includes(sticky.tag2)) {
    const tooltip = getTagTooltip(sticky.tag1, sticky.tag2);
    if (tooltip) {
      // Generate consistent label: "Expand Character", "Expand Scene", etc.
      const capitalizedTag = sticky.tag2.charAt(0).toUpperCase() + sticky.tag2.slice(1);
      expansionItems.push({
        label: `⚡ Expand ${capitalizedTag}`,
        task: buildExpansionPrompt(sticky.tag2, sticky.content)
      });
    }
  }
  
  return [...expansionItems, ...baseItems];
};

  const renderSubBar = () => {
  if (Object.keys(tagDomains).length === 0) return null;
  
  return (
    <div className="sticky-sub-bar">
      {/* Tag Section */}
      <div className="sub-bar-section">
        <span className="sub-bar-label">Domain:</span>
        <TagDropdown
          value={sticky.tag1 || ''}
          onChange={handleDomainChange}
          options={getDomainOptions}
          placeholder="None"
          type="domain"
          className="sub-bar-dropdown"
          title={getDomainTooltip(sticky.tag1)} // Add this
        />
      </div>
      
      <div className="sub-bar-section">
        <span className="sub-bar-label">Tag:</span>
        <TagDropdown
          value={sticky.tag2 || ''}
          onChange={handleTagChange}
          options={getTagsForDomain(sticky.tag1)}
          placeholder="None"
          type="tag"
          disabled={!sticky.tag1}
          className="sub-bar-dropdown"
          title={getTagTooltip(sticky.tag1, sticky.tag2)} // Add this
        />
      </div>
      
      {/* Move some controls from main drag bar here */}
      <div className="sub-bar-section" style={{ marginLeft: 'auto' }}>
         <button 
              className="sub-bar-button"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyContent();
              }} 
              title="Copy all content"
            >
              📋
            </button>
        <button 
          className="sub-bar-button"
          onClick={(e) => {
            e.stopPropagation();
            toggleTextSize();
          }} 
          title={`Text size: ${textSize}`}
        >
          {getTextSizeIcon(textSize)}
        </button>
        
        {!isInMainPanel && (
          <button 
            className="sub-bar-button"
            onClick={(e) => {
              e.stopPropagation();
              togglePerspective();
            }} 
            title={perspectiveEnabled ? "Disable 3D effect" : "Enable 3D effect"}
          >
            {perspectiveEnabled ? '🔍' : '🔎'}
          </button>
        )}
      </div>
    </div>
  );
}; 


  return (
    <div
      className={`sticky ${isMinimized ? 'minimized' : ''} ${sticky.type === 'ai' ? 'ai-sticky' : ''} ${!isInMainPanel ? 'right-panel-perspective' : ''} ${perspectiveEnabled ? 'perspective-enabled' : ''}`}
      style={{
        top: isInMainPanel ? sticky.main_y : sticky.y,
        left: isInMainPanel ? sticky.main_x : sticky.x,
        width: isInMainPanel ? sticky.main_width : sticky.width,
        height: isInMainPanel ? sticky.main_height : sticky.height,
        zIndex: sticky.zIndex
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (sticky.note_id) {
          onFocus?.();
        }
      }}
       onDragOver={handleStickyDragOver}
      onDrop={handleStickyDrop}
      onPaste={handlePaste}  // ← ADD THIS
      tabIndex={0}           // ← ADD THIS
    >


      <div className="drag-bar" onMouseDown={startDrag} onDoubleClick={toggleMinimize}>
        <div className="sticky-pin">
        <input 
          type="checkbox"
          checked={isSelected} // Changed from isPinned
          onChange={handleSelectToggle} // Changed from handlePinToggle
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          title="Select for AI/Pin/Delete"
        />
      </div>
        <input 
            type="text"
            className="sticky-title-input"
            value={localTitle}
            onChange={(e) => {
              //console.log('🎯 TITLE onChange - calling updateSticky with:', { title: e.target.value }); // ADD THIS
              setLocalTitle(e.target.value);
              updateSticky(sticky.id, { title: e.target.value });
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            placeholder="Untitled..."
          />
          
        <div className="sticky-controls">
          {!isInMainPanel && (
            <button onClick={(e) => {
              e.stopPropagation();
              togglePerspective();
            }} title={perspectiveEnabled ? "Disable 3D effect" : "Enable 3D effect"}>
              {perspectiveEnabled ? '📐' : '📏'}
            </button>
          )}
 

            <button onClick={(e) => {
              e.stopPropagation();
              toggleExpand();
            }}>
              {isExpanded ? '⊙' : '⊕'}
            </button>

            <button onClick={(e) => {
              e.stopPropagation();
              onToggleDisplayMode(sticky.id, 'list');
            }} title="Send to List">
              ▼
            </button>

            <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAIMenu(prev => !prev);
                  }} 
                  title="Run AI task on this sticky"
                >
                  🤖
                </button>

            <button onClick={() => {
                console.log('🟢 X BUTTON CLICKED');
                setIsDeleteModalOpen(true);
                console.log('🟢 Modal should be open:', true);
              }}>✖</button>
          </div>

      </div>
    {!isMinimized && renderSubBar()}

      {!isMinimized && (
  <>
{sticky.type === 'image' ? (
  <div className="sticky-image-container" style={{
    position: 'relative',
    width: '100%',
    height: 'calc(100% - 80px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f5',
    overflow: imageZoom > 1 ? 'auto' : 'hidden'  // ✅ Scroll only when zoomed
  }}>
    <img 
      src={sticky.image_url} 
      alt="Sticky image"
      style={{
        maxWidth: imageZoom > 1 ? 'none' : '100%',
        maxHeight: imageZoom > 1 ? 'none' : '100%',
        objectFit: 'contain',
        borderRadius: '4px',
        transform: `scale(${imageZoom})`,
        transformOrigin: 'center',
        transition: 'transform 0.2s ease'
      }}
    />
    
    {/* Simple Zoom Toggle */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        setImageZoom(prev => prev === 1 ? 1.5 : 1); // Toggle 100% ↔ 120%
      }}
      style={{
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        padding: '6px 12px',
        fontSize: '14px',
        cursor: 'pointer',
        background: 'rgba(255, 255, 255, 0.95)',  
        border: '1px solid #ddd',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontWeight: imageZoom > 1 ? 'bold' : 'normal'
      }}
      title={imageZoom === 1 ? "Zoom to 150%" : "Reset to 100%"}
    >
     {imageZoom === 1 ? '🔍+' : '🔍−'}
    </button>
  </div>
)
: (
      // TEXT MODE (existing code)
      <>
        {isEditing ? (
          // Edit mode - plain textarea
          <textarea
            value={sticky.content}
            onChange={(e) => updateSticky(sticky.id, { content: e.target.value })}
            onClick={(e) => e.stopPropagation()}
            onBlur={() => setIsEditing(false)}
            onFocus={() => {
              onFocus?.();
              if (isInMainPanel) {
                updateSticky(sticky.id, {
                  main_x: sticky.main_x,
                  main_y: sticky.main_y,
                });
              }
            }}
            autoFocus
            style={{ fontSize: getTextSizeValue(textSize) }}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'copy';
              e.dataTransfer.dropEffect = 'copy';
            }}
          />
        ) : (
          // Display mode - formatted content
          <div
            className="sticky-formatted-content"
            style={{ 
              fontSize: getTextSizeValue(textSize),
              userSelect: 'text',
              cursor: 'text'
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            dangerouslySetInnerHTML={{ 
              __html: formatText(sticky.content) 
            }}
          />
        )}
      </>
    )}
    <div className="resize-handle" onMouseDown={startResize} />
  </>
)}


           {showStickyDropMenu && (
        <div 
          className="sticky-drop-menu" 
          style={{ 
            position: 'absolute', 
            left: stickyDropPosition.x, 
            top: stickyDropPosition.y,
            zIndex: 10001,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            padding: '8px',
            minWidth: '160px'
          }}
        >
          <div style={{ padding: '4px 12px', fontWeight: 'bold', fontSize: '12px' }}>
            Paste text into this note:
          </div>
          
          <button 
            onClick={() => handleStickyDropAction('paste')}
            style={{ 
              display: 'block', width: '100%', textAlign: 'left',
              padding: '6px 12px', margin: '2px 0', border: 'none',
              borderRadius: '3px', cursor: 'pointer', backgroundColor: 'transparent'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            📎 Append to end
          </button>
          
          <button 
            onClick={() => handleStickyDropAction('prepend')}
            style={{ 
              display: 'block', width: '100%', textAlign: 'left',
              padding: '6px 12px', margin: '2px 0', border: 'none',
              borderRadius: '3px', cursor: 'pointer', backgroundColor: 'transparent'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            📌 Add to beginning
          </button>
          
          <button 
            onClick={() => handleStickyDropAction('replace')}
            style={{ 
              display: 'block', width: '100%', textAlign: 'left',
              padding: '6px 12px', margin: '2px 0', border: 'none',
              borderRadius: '3px', cursor: 'pointer', backgroundColor: 'transparent'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            🔄 Replace content
          </button>
        </div>
      )}


<ConfirmModal
  isOpen={isDeleteModalOpen}
  onClose={() => setIsDeleteModalOpen(false)}
  onConfirm={handleDelete}
  title="Delete Sticky Note?"
  message="Are you sure you want to delete this sticky note?"
  confirmText="Delete"
  cancelText="Cancel"
  type="danger"
/>

     {customPromptMode && (
  <div 
    className="ai-custom-prompt-overlay"
    style={{
      position: 'absolute',
      top: '45px',
      right: (isInMainPanel ? sticky.main_width : sticky.width) < 340 ? undefined : '5px',
      left: (isInMainPanel ? sticky.main_width : sticky.width) < 340 ? '5px' : undefined,
      zIndex: 10000,
      backgroundColor: '#fff',  
      border: '1px solid #ccc',
      borderRadius: '6px',
      padding: '10px',
      width: '320px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.25)'
    }}
  >
    <textarea
      value={customPromptText}
      onChange={(e) => setCustomPromptText(e.target.value)}
      rows={3}
      placeholder="Enter your custom AI task..."
      style={{
        width: '100%',
        fontSize: '13px',
        borderRadius: '4px',
        padding: '6px',
        border: '1px solid #bbb',
        resize: 'none'
      }}
    />
    <div style={{ marginTop: '8px', textAlign: 'right' }}>
      <button
        style={{ marginRight: '6px' }}
        onClick={() => {
          setCustomPromptText('');
          setCustomPromptMode(false);
        }}
      >
        Cancel
      </button>
      
      <button
  onClick={async () => {
    if (!customPromptText.trim()) return;
  
    // 🚀 Close the overlay *immediately*
    setCustomPromptMode(false);
  
    // 🔄 Start spinner
    setIsProcessingAI(true);
  
    try {
      // Clear any previous errors or outputs
      setAiError(null);
      setPendingAIOutput(null);
      
      const response = await sendStickySubmenuToAI(
          [{ id: sticky.id, content: sticky.content, type: sticky.type }],
          customPromptText.trim()  // Keep original prompt, backend should handle context
        );
                
      if (!response) {
        setAiError("AI did not return a result for your custom prompt.");
        return;
      }
  
      setPendingAIOutput(response); // Trigger choice menu
    } catch (err) {
      setAiError(err.message || "Failed to generate AI response");
      console.error(err);
    } finally {
      setCustomPromptText('');
      setIsProcessingAI(false);
    }
  }}
      >
        Run
      </button>
    </div>
  </div>
)}


      {showAIMenu && (
  <div 
    ref={aiMenuRef}
    className="ai-menu-popover"
    style={{
      position: 'absolute',
      top: '42px',
      right: '5px',
      backgroundColor: '#fff',
      border: '1px solid #ccc',
      borderRadius: '6px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
      padding: '6px 0',
      zIndex: 9999,
      width: '200px'  // Made slightly wider for lightning bolt options
    }}
  >
    {getTagAwareMenuItems().map((option, idx) => (
      <div 
        key={idx}
        onClick={(e) => {
          e.stopPropagation();
          setShowAIMenu(false);
          handleAISelection(option.task);
        }}
        style={{
          padding: '8px 12px',
          cursor: 'pointer',
          fontSize: '14px',
          borderBottom: idx < getTagAwareMenuItems().length - 1 ? '1px solid #eee' : 'none'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
      >
        {option.label}
      </div>
    ))}
  </div>
)}


{isProcessingAI && (
  <div 
    className="processing-indicator"
    style={{
      position: 'absolute',
      top: '45px', 
      right: '10px',
      width: '32px',
      height: '32px',
      zIndex: 10000,
      pointerEvents: 'none'
    }}
  >
    <svg viewBox="0 0 100 100" width="100%" height="100%">
      <path
        className="snowflake-path"
        d="M50 15L50 85M15 50L85 50M26 26L74 74M26 74L74 26"
        stroke="#4f46e5"
        strokeWidth="6"
        strokeLinecap="round"
      />
    </svg>
  </div>
)}
{pendingAIOutput && (
 <div className="ai-output-decision">
    <div style={{ marginBottom: '6px' }}>
      AI generated a result. What do you want to do with it?
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
      <button onClick={() => {
        updateSticky(sticky.id, {
          content: `${sticky.content}\n\n--- AI Output ---\n${pendingAIOutput}`
        });
        setPendingAIOutput(null);
      }}>
        Append
      </button>
      <button onClick={() => {
        updateSticky(sticky.id, {
          content: pendingAIOutput
        });
        setPendingAIOutput(null);
      }}>
        Replace
      </button>
      <button
  onClick={async () => {
    try {
      // STEP 1: Ask parent to create a new sticky with high z-index
      const topZIndex = (props.maxZIndex || 100) + 10;
      const newStickyId = await props.addSticky?.(topZIndex, 'regular');

      // STEP 2: Proceed only if sticky was created
      if (newStickyId) {
        // STEP 3: Update sticky content and metadata
        await updateSticky(newStickyId, {
          content: pendingAIOutput,
          zIndex: topZIndex,
          note_id: sticky.note_id, // Maintain same note
          type: 'regular'
        });

        // STEP 4: Bring it to front
        props.onFocus?.(newStickyId); // Optional: only if this handler takes an ID
      }
    } catch (err) {
      console.error("❌ Failed to create new sticky from AI output:", err);
    } finally {
      setPendingAIOutput(null); // Clear the AI output buffer
    }
  }}
>
  New Note
</button>
    </div>
  </div>
)}
{aiError && (
  <div className="ai-output-decision" style={{backgroundColor: '#fef2f2', borderColor: '#f56565'}}>
    <div 
      style={{ marginBottom: '6px', color: '#e53e3e', fontWeight: '500' }}
      dangerouslySetInnerHTML={{ __html: aiError }}
    />
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
      <button 
        onClick={() => setAiError(null)}
        style={{ backgroundColor: '#e53e3e', color: 'white' }}
      >
        Dismiss
      </button>
    </div>
  </div>
)}
    </div>
  );
};




// Comprehensive memo comparison function that works for both panels
const areStickyPropsEqual = (prevProps, nextProps) => {
  // First check common properties
  const basicPropsEqual = 
    prevProps.sticky.id === nextProps.sticky.id &&
    prevProps.sticky.content === nextProps.sticky.content &&
    prevProps.sticky.zIndex === nextProps.sticky.zIndex &&
    prevProps.sticky.is_minimized === nextProps.sticky.is_minimized &&
    prevProps.isPinned === nextProps.isPinned;

  // Check panel-specific properties
  const mainPanelPropsEqual = 
    prevProps.isInMainPanel === nextProps.isInMainPanel &&
    (!prevProps.isInMainPanel || (
      prevProps.sticky.main_x === nextProps.sticky.main_x &&
      prevProps.sticky.main_y === nextProps.sticky.main_y &&
      prevProps.sticky.main_width === nextProps.sticky.main_width &&
      prevProps.sticky.main_height === nextProps.sticky.main_height
    ));

  // Check right panel properties
  const rightPanelPropsEqual = 
    prevProps.isInMainPanel === nextProps.isInMainPanel &&
    (prevProps.isInMainPanel || (
      prevProps.sticky.x === nextProps.sticky.x &&
      prevProps.sticky.y === nextProps.sticky.y &&
      prevProps.sticky.width === nextProps.sticky.width &&
      prevProps.sticky.height === nextProps.sticky.height
    ));

  // Check handler functions
  const handlersEqual = 
    prevProps.updateSticky === nextProps.updateSticky &&
    prevProps.removeSticky === nextProps.removeSticky &&
    prevProps.onFocus === nextProps.onFocus &&
    prevProps.onToggleDisplayMode === nextProps.onToggleDisplayMode &&
    prevProps.onPinChange === nextProps.onPinChange &&
    prevProps.maxZIndex === nextProps.maxZIndex;

  return basicPropsEqual && mainPanelPropsEqual && rightPanelPropsEqual && handlersEqual;
};

export default React.memo(StickyNote, areStickyPropsEqual);